import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAuthStore } from "../../store/authStore";
import { useThemeStore } from "../../store/themeStore";
import PlugDLogo from "../common/PlugDLogo";

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuthStore();
  const { theme } = useThemeStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("login_toast_welcome", "Welcome back!"));
      navigate("/");
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorMsg = (err as Record<string, any>)?.response?.data?.detail || t("login_error_default", "Login failed");
      toast.error(String(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const features = [
    t("login_feature_1", "Schedule posts across all platforms"),
    t("login_feature_2", "Manage multiple accounts seamlessly"),
    t("login_feature_3", "Track analytics and engagement"),
    t("login_feature_4", "Collaborate with your team"),
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Panel - Branded Section (Hidden on Mobile) */}
      <div className={`hidden lg:flex lg:w-1/2 ${theme.colors.primary} ${theme.colors.primaryGradientFrom} ${theme.colors.primaryGradientTo} flex-col justify-between p-12 text-white relative overflow-hidden`}>
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-5 rounded-full -ml-48 -mb-48"></div>

        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <PlugDLogo size={56} className="rounded-xl opacity-90" />
            <h2 className="text-2xl font-bold">PlugD</h2>
          </div>

          {/* Tagline */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold mb-4 leading-tight">{t("login_tagline_title", "AI-Powered Social Media Management")}</h1>
            <p className="text-lg opacity-90">{t("login_tagline_subtitle", "Streamline your social presence and grow faster with PlugD")}</p>
          </div>

          {/* Features */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-base opacity-90">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Text */}
        <div className="relative z-10 text-sm opacity-75">
          <p>{t("login_footer_text", "Join thousands of content creators and agencies worldwide")}</p>
        </div>
      </div>

      {/* Right Panel - Form Section */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo (Visible on Mobile) */}
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <PlugDLogo size={48} className="rounded-xl" />
            <span className="text-2xl font-bold text-gray-900">PlugD</span>
          </div>

          {/* Form Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t("login_form_title", "Welcome back")}</h1>
            <p className="text-gray-600">{t("login_form_subtitle", "Sign in to your PlugD account to continue")}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("login_label_email", "Email address")}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 transition-all focus:outline-none focus:ring-2 focus:border-transparent`}
                style={{ outlineColor: theme.hex.primaryRing }}
                placeholder={t("login_placeholder_email", "you@example.com")}
                required
              />
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
                  {t("login_label_password", "Password")}
                </label>
                <button type="button" onClick={() => toast(t("login_toast_reset", "Password reset coming soon!"), { icon: "🔒" })} className={`text-sm font-medium ${theme.colors.primaryText} hover:opacity-80 transition-opacity`}>
                  {t("login_button_forgot", "Forgot?")}
                </button>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 transition-all focus:outline-none focus:ring-2 focus:border-transparent`}
                style={{ outlineColor: theme.hex.primaryRing }}
                placeholder={t("login_placeholder_password", "Enter your password")}
                required
              />
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-4 ${theme.colors.primary} hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t("login_button_signing", "Signing in...")}
                </span>
              ) : (
                t("login_button_signin", "Sign In")
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t("login_divider_text", "New to PlugD?")}</span>
            </div>
          </div>

          {/* Sign Up Link */}
          <p className="text-center text-gray-600">
            <Link to="/register" className={`font-semibold ${theme.colors.primaryText} hover:opacity-80 transition-opacity`}>
              {t("login_link_signup", "Create a free account")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
